import React, { useState, useEffect } from 'react';
import { getArbicsxMonthlyStats, addArbicsxMonthlyCapital } from '../../lib/api';
import { useModule } from '../../contexts/ModuleContext';
import { formatCurrency, getMonthName } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { RefreshCw, Percent, ChevronDown, ChevronRight, Pencil, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';

// anello: solo aspetto, la percentuale arriva gia' calcolata dal backend
const ringStyle = (percent) => ({
  '--p': Math.min(Math.abs(percent || 0), 100),
  '--rc': (percent || 0) > 0 ? 'var(--ht-green)' : (percent || 0) < 0 ? 'var(--ht-red)' : 'var(--ht-dim)'
});

export default function ArbicsxPercentualiMensili() {
  const { impersonatedUser, isImpersonating } = useModule();
  const [stats, setStats] = useState({ 
    months: [], 
    average_percent: 0, 
    total_profit: 0, 
    first_month_capital: 0, 
    total_return_percent: 0 
  });
  const [loading, setLoading] = useState(true);
  const [expandedYears, setExpandedYears] = useState({});
  const [showCapitalModal, setShowCapitalModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [capitalInput, setCapitalInput] = useState('');

  const impersonateId = isImpersonating ? impersonatedUser?.id : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getArbicsxMonthlyStats(impersonateId);
      setStats(response.data);
      
      // Auto-expand current year
      const currentYear = new Date().getFullYear().toString();
      setExpandedYears({ [currentYear]: true });
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [impersonateId]);

  // Group months by year
  const monthsByYear = (stats.months || []).reduce((acc, month) => {
    const year = month.month?.substring(0, 4) || 'unknown';
    if (!acc[year]) acc[year] = [];
    acc[year].push(month);
    return acc;
  }, {});

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const handleSaveCapital = async () => {
    if (!capitalInput || parseFloat(capitalInput) <= 0) {
      toast.error('Inserisci un capitale valido');
      return;
    }

    try {
      await addArbicsxMonthlyCapital({
        month: selectedMonth,
        capital: parseFloat(capitalInput)
      }, impersonateId);
      toast.success('Capitale aggiornato');
      setShowCapitalModal(false);
      setSelectedMonth(null);
      setCapitalInput('');
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const openCapitalModal = (month, currentCapital) => {
    setSelectedMonth(month);
    setCapitalInput(currentCapital > 0 ? currentCapital.toString() : '');
    setShowCapitalModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ht-page-h1">Performance Mensile</h1>
          <p className="ht-page-sub">
            Arbicsx {isImpersonating && `- ${impersonatedUser?.name}`} · performance mensili rispetto al capitale
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          className="ht-btn self-start sm:self-auto"
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Aggiorna
        </button>
      </div>

      {/* Sintesi con anelli */}
      <div className="ht-panel">
        <div className="ht-hero">
          <div className="flex items-center gap-4 min-w-0" data-testid="average-card">
            <div className="ht-ringpct ht-ringpct-lg" style={ringStyle(stats.average_percent)}>
              <span>{(stats.average_percent || 0).toFixed(2)}%</span>
            </div>
            <div className="min-w-0">
              <span className="ht-hero-label">Media % mensile</span>
              <p className="ht-item-s mt-1">Media aritmetica</p>
            </div>
          </div>

          {stats.first_month_capital > 0 && (
            <div className="ht-hero-side ht-rows" data-testid="total-return-card">
              <div className="ht-row">
                <span className="ht-row-label"><TrendingUp size={12} /> Rendimento totale</span>
                <span className={`ht-row-value ${(stats.total_return_percent || 0) >= 0 ? 'ht-pos' : 'ht-alert'}`}>
                  {(stats.total_return_percent || 0) >= 0 ? '+' : ''}{(stats.total_return_percent || 0).toFixed(2)}%
                </span>
              </div>
              <div className="ht-row">
                <span className="ht-row-label">Profitto</span>
                <span className="ht-row-value">{formatCurrency(stats.total_profit || 0)}</span>
              </div>
              <div className="ht-row">
                <span className="ht-row-label">Capitale iniziale</span>
                <span className="ht-row-value">{formatCurrency(stats.first_month_capital)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Anni */}
      {Object.keys(monthsByYear).length === 0 ? (
        <div className="ht-panel ht-empty">
          <Percent size={36} className="mx-auto mb-3 opacity-40" />
          <p>Nessun dato mensile disponibile</p>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.keys(monthsByYear).sort().reverse().map(year => {
            const yearMonths = monthsByYear[year];
            const yearTotal = yearMonths.reduce((s, m) => s + (m.gross || 0), 0);
            const isExpanded = expandedYears[year];
            
            return (
              <div key={year} className="ht-acc" data-testid={`year-${year}`}>
                <button
                  type="button"
                  onClick={() => toggleYear(year)}
                  className="ht-acc-head"
                  data-testid={`year-toggle-${year}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {isExpanded
                      ? <ChevronDown size={14} className="ht-chev" />
                      : <ChevronRight size={14} className="ht-chev" />}
                    <Calendar size={14} className="text-[#5f6672]" />
                    <span className="ht-acc-title">{year}</span>
                    <span className="ht-tag">{yearMonths.length} mesi</span>
                  </span>
                  <span className={`ht-item-n ${yearTotal >= 0 ? 'ht-pos' : 'ht-alert'}`}>
                    {yearTotal >= 0 ? '+' : ''}{formatCurrency(yearTotal)}
                  </span>
                </button>
                
                {isExpanded && (
                  <div className="ht-acc-body p-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                    {yearMonths.sort((a, b) => b.month.localeCompare(a.month)).map(month => (
                      <div
                        key={month.month}
                        className="flex items-center gap-3 p-3 rounded-[12px] border border-[#282c34] bg-[#11161d] min-w-0"
                        data-testid={`month-card-${month.month}`}
                      >
                        <div className="ht-ringpct" style={ringStyle(month.percent)}>
                          <span>{(month.percent || 0).toFixed(1)}%</span>
                        </div>
                        <div className="min-w-0 flex-1 ht-rows">
                          <div className="ht-row" style={{ paddingTop: 0 }}>
                            <span className="ht-acc-title truncate">{getMonthName(month.month)}</span>
                          </div>
                          <div className="ht-row">
                            <span className="ht-row-label">Profitto</span>
                            <span className={`ht-row-value ${(month.gross || 0) >= 0 ? 'ht-pos' : 'ht-alert'}`}>
                              {formatCurrency(month.gross || 0)}
                            </span>
                          </div>
                          <div className="ht-row">
                            <span className="ht-row-label">Capitale</span>
                            <span className="flex items-center gap-1.5">
                            <span className={`ht-row-value ${!(month.capital > 0) ? 'ht-warn' : ''}`}>
                                {month.capital > 0 ? formatCurrency(month.capital) : '-'}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCapitalModal(month.month, month.capital || 0);
                                }}
                                className="ht-btn ht-btn-icon"
                                aria-label="Modifica capitale"
                                data-testid={`edit-capital-${month.month}`}
                              >
                                <Pencil size={11} />
                              </button>
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Capital Modal */}
      <Dialog open={showCapitalModal} onOpenChange={setShowCapitalModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="ht-panel-title text-base">Modifica Capitale Mensile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="ht-row">
              <span className="ht-row-label">Mese</span>
              <span className="ht-row-value">{selectedMonth && getMonthName(selectedMonth)}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Capitale ($)</Label>
              <Input
                type="number"
                value={capitalInput}
                onChange={(e) => setCapitalInput(e.target.value)}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="Es: 10000"
                data-testid="capital-input"
              />
              <p className="text-xs text-[#5f6672]">
                Inserisci il capitale investito per questo mese. La percentuale verrà calcolata automaticamente.
              </p>
            </div>
          </div>
          <DialogFooter>
            <button type="button" className="ht-btn" onClick={() => setShowCapitalModal(false)}>
              Annulla
            </button>
            <button type="button" onClick={handleSaveCapital} className="ht-btn ht-btn-primary" data-testid="save-capital-btn">
              Salva
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
